import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../helpers/api";
import { totpErrorMessage } from "../components/totp-error";

const NOT_AUTHORIZED_MSG =
  "You are not authorized to charge. Ask a manager to add you.";

type Me = {
  authorized: boolean;
  enrolled: boolean;
  isManager: boolean;
  unlockedUntil: string | null;
};

// Step-up unlock gate: one code opens a 15-min window; charges within it run
// without a per-action prompt. `runCharge(fn)` runs immediately if unlocked,
// otherwise stashes the action and routes to the right screen (enroll QR /
// unlock prompt / not-authorized notice), resuming the charge once unlocked.
export function useChargeUnlock() {
  const queryClient = useQueryClient();
  const { data: me } = useQuery<Me>({
    queryKey: ["charge-auth-me"],
    queryFn: () => api.chargeAuth.me(),
    refetchOnWindowFocus: false,
  });
  const unlockedUntil = me?.unlockedUntil ? new Date(me.unlockedUntil) : null;
  const isUnlocked = !!unlockedUntil && unlockedUntil.getTime() > Date.now();

  const [showUnlock, setShowUnlock] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [blocked, setBlocked] = useState<string | undefined>();
  const [unlockError, setUnlockError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  // Prompt to unlock up front when opening Billing already enrolled but locked.
  useEffect(() => {
    if (me?.authorized && me?.enrolled && !isUnlocked) setShowUnlock(true);
  }, [me?.authorized, me?.enrolled, isUnlocked]);

  const runCharge = (fn: () => void) => {
    setPending(() => fn);
    if (me && !me.authorized) {
      setPending(null);
      setBlocked(NOT_AUTHORIZED_MSG);
    } else if (me && !me.enrolled) {
      setShowEnroll(true);
    } else if (isUnlocked) {
      fn();
    } else {
      setUnlockError(undefined);
      setShowUnlock(true);
    }
  };

  // Charge mutation error handler (e.g. the window expired mid-run).
  const onChargeError = (e: unknown) => {
    const { code } = totpErrorMessage(e);
    if (code === "charge_locked") {
      // Keep `pending` so the charge resumes after re-unlocking.
      setShowUnlock(true);
    } else if (code === "enrollment_required") {
      setShowEnroll(true);
    } else if (code === "not_authorized") {
      setPending(null);
      setBlocked(NOT_AUTHORIZED_MSG);
    } else {
      // A real charge failure (card/network/server). Drop the deferred action
      // so a later unlock can't silently re-run it; the mutation's toast informs.
      setPending(null);
    }
  };

  const submitUnlock = async (code: string) => {
    setSubmitting(true);
    setUnlockError(undefined);
    try {
      await api.chargeAuth.unlock(code);
      await queryClient.invalidateQueries({ queryKey: ["charge-auth-me"] });
      setShowUnlock(false);
      const fn = pending;
      setPending(null);
      fn?.(); // resume the deferred charge, if any
    } catch (e) {
      const { code: c, message } = totpErrorMessage(e);
      if (c === "enrollment_required") {
        setShowUnlock(false);
        setShowEnroll(true);
      } else {
        setUnlockError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    me,
    isUnlocked,
    unlockedUntil,
    runCharge,
    onChargeError,
    done: () => setPending(null),
    showUnlock,
    promptUnlock: () => {
      setUnlockError(undefined);
      setShowUnlock(true);
    },
    submitUnlock,
    submitting,
    unlockError,
    closeUnlock: () => {
      setShowUnlock(false);
      setPending(null);
    },
    showEnroll,
    onEnrolled: () => {
      queryClient.invalidateQueries({ queryKey: ["charge-auth-me"] });
      setShowEnroll(false);
      setShowUnlock(true); // enrolled, now unlock
    },
    cancelEnroll: () => {
      setShowEnroll(false);
      setPending(null);
    },
    blocked,
    closeBlocked: () => setBlocked(undefined),
  };
}
